import { useState, useEffect } from "react";
import api from "../../api/axios";
import {
    ShieldAlert,
    CheckCircle,
    XCircle,
    AlertTriangle
} from "lucide-react";
import toast from "react-hot-toast";

export default function SafetyQueue() {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchReports = async () => {
        try {
            const res = await api.get("/admin/reports");
            setReports(res.data);
        } catch (err) {
            toast.error("Failed to load queue");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, []);

    const handleAction = async (reportId, action, userId) => {
        if (!window.confirm(`Are you sure you want to ${action} this report?`)) return;

        try {
            if (action === "DISMISS") {
                await api.post(`/admin/reports/${reportId}/dismiss`, {
                    reason: "Dismissed by Admin"
                });
            } else if (action === "BAN") {
                await api.post(`/admin/users/${userId}/action`, {
                    action,
                    reason: "Admin manual ban from Queue"
                });
            }

            toast.success(`Action: ${action} Successful`);
            fetchReports(); // Refresh
        } catch (err) {
            console.error(err);
            toast.error("Action failed");
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-3xl font-light text-white">
                    Safety <span className="font-bold text-red-500">Queue</span>
                </h2>
                <p className="text-gray-500 mt-2">Review flags and dispense justice.</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Scanning frequency...</div>
                ) : reports.length === 0 ? (
                    <div className="p-12 flex flex-col items-center justify-center text-gray-500">
                        <CheckCircle size={48} className="text-green-500 mb-4 opacity-50" />
                        <p className="text-lg">All Quiet on the Front.</p>
                        <p className="text-sm opacity-60">No pending reports.</p>
                    </div>
                ) : (
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-black/40 text-xs uppercase tracking-wider text-gray-500">
                                <th className="p-4 font-medium">Suspect</th>
                                <th className="p-4 font-medium">Evidence (Reason)</th>
                                <th className="p-4 font-medium">Reporter</th>
                                <th className="p-4 font-medium">History</th>
                                <th className="p-4 font-medium text-right">Judgment</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5 text-sm">
                            {reports.map((report) => (
                                <tr key={report.id} className="hover:bg-white/5 transition-colors group">
                                    <td className="p-4">
                                        <div className="font-bold text-white">{report.reported?.name || "Unknown"}</div>
                                        <div className="text-gray-500 text-xs">{report.reported?.email}</div>
                                    </td>
                                    <td className="p-4 max-w-md">
                                        <div className="flex flex-col gap-2">
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 w-fit rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                                                <AlertTriangle size={12} />
                                                {report.reason}
                                            </span>
                                            {report.message?.content && (
                                                <div className="p-2 bg-black/40 rounded border border-gray-800 text-gray-300 text-xs italic">
                                                    "{report.message.content}"
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4 text-gray-400">
                                        {report.reporter?.name}
                                    </td>
                                    <td className="p-4">
                                        <div className="text-gray-300 font-mono">
                                            {report.reported?.reportCount || 1} Flags
                                        </div>
                                    </td>
                                    <td className="p-4 text-right space-x-2 opacity-100 lg:opacity-50 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => handleAction(report.id, "BAN", report.reportedId)}
                                            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-colors shadow-lg shadow-red-900/20"
                                        >
                                            Ban Hammer
                                        </button>
                                        <button
                                            onClick={() => handleAction(report.id, "DISMISS")}
                                            className="px-3 py-1.5 border border-gray-600 hover:bg-gray-700 text-gray-300 rounded-lg text-xs font-medium transition-colors"
                                        >
                                            Dismiss
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
